import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Badge } from '@/shared/ui/Badge';
import { Dialog } from '@/shared/ui/Dialog';
import { Icon } from '@/shared/ui/Icon';
import { useToast } from '@/shared/ui/Toast';
import { deleteAccount, logout } from '@/shared/api/auth';
import { useAuthStore } from '@/shared/auth/store';
import { avatarColorFor } from '@/shared/lib/avatarColor';
import { PayoutAccountCard } from '@/features/profile/PayoutAccountCard';
import { PushNotificationCard } from '@/features/profile/PushNotificationCard';
import { unregisterLastPushToken } from '@/features/push/usePushRegistration';
import { saveFavoriteRegions } from '@/features/place/regions';
import { getStoredTheme, setStoredTheme, type ThemeMode } from '@/shared/lib/theme';

const PROVIDER_LABEL: Record<string, string> = {
  PHONE: '휴대폰 번호',
  EMAIL: '이메일',
  KAKAO: '카카오',
  GOOGLE: '구글',
  NAVER: '네이버',
};

function providerIconName(provider: string): 'mail' | 'user' | 'lock' {
  if (provider === 'EMAIL') return 'mail';
  if (provider === 'PHONE') return 'lock';
  return 'user';
}

export default function ProfilePage() {
  const navigate = useNavigate();
  const toast = useToast();
  const user = useAuthStore((s) => s.user);
  const clearAuth = useAuthStore((s) => s.logout);

  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [theme, setTheme] = useState<ThemeMode>(() => getStoredTheme());

  function chooseTheme(nextTheme: ThemeMode) {
    setTheme(nextTheme);
    setStoredTheme(nextTheme);
  }


  const logoutMut = useMutation({
    mutationFn: async () => {
      // 로그아웃 전 토큰이 유효할 때 푸시 토큰을 best-effort 로 해제
      await unregisterLastPushToken();
      try {
        await logout();
      } catch {
        // ignore — clear local anyway
      }
    },
    onSuccess: () => {
      saveFavoriteRegions([]); // 다음 계정이 이전 선호 지역을 보지 않도록
      clearAuth();
      toast.show('로그아웃했어요.');
      navigate('/login', { replace: true });
    },
  });

  const deleteMut = useMutation({
    mutationFn: deleteAccount,
    onSuccess: () => {
      clearAuth();
      toast.show('계정을 삭제했어요. 그동안 함께해줘서 고마워요.');
      navigate('/login', { replace: true });
    },
    onError: () => {
      setConfirmingDelete(false);
      toast.show('탈퇴 처리에 실패했어요. 다시 시도해주세요.');
    },
  });

  if (!user) return null;
  const initial = user.nickname.trim().charAt(0) || '나';
  const providerLabel = PROVIDER_LABEL[user.provider] ?? user.provider;

  return (
    <section className="space-y-6 pb-2" aria-label="내 프로필">
      <header className="pt-2">
        <p className="text-[12.5px] font-semibold tracking-[-0.1px] text-primary-text">계정 · 알림 · 정산 설정</p>
        <h1 className="mt-1 text-[28px] font-bold leading-[1.05] tracking-[-0.8px] text-ink">마이</h1>
      </header>

      <section className="rounded-[22px] border border-surface-border/70 bg-surface/60 backdrop-blur-sm p-4" aria-label="프로필 요약">
        <div className="flex items-center gap-3">
          <span
            className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-[18px] text-[20px] font-semibold text-white shadow-[0_8px_18px_rgba(120,201,87,0.18)]"
            style={{ backgroundColor: avatarColorFor(user.id) }}
          >
            {initial}
          </span>
          <div className="min-w-0">
            <p className="truncate text-[19px] font-semibold tracking-[-0.5px] text-ink">{user.nickname}</p>
            <p className="mt-1 text-[12px] text-ink-muted">
              {user.provider === 'EMAIL' ? '이메일 계정' : `${providerLabel} 로그인`}
            </p>
          </div>
        </div>
        <div className="mt-3.5 grid grid-cols-2 gap-2">
          <div className="rounded-[13px] bg-surface-muted px-3 py-2.5">
            <p className="text-[10px] font-semibold text-ink-muted">닉네임</p>
            <p className="mt-1 truncate text-[14px] font-semibold text-ink">{user.nickname}</p>
          </div>
          <div className="rounded-[13px] bg-surface-muted px-3 py-2.5">
            <p className="text-[10px] font-semibold text-ink-muted">로그인</p>
            <p className="mt-1 truncate text-[14px] font-semibold text-ink">{providerLabel}</p>
          </div>
        </div>
      </section>
      <section aria-labelledby="profile-theme-heading" className="space-y-2">
        <h2 id="profile-theme-heading" className="px-0.5 text-[11px] font-semibold uppercase tracking-[0.04em] text-ink-muted">화면 테마</h2>
        <div className="grid grid-cols-2 gap-2 rounded-[22px] border border-surface-border/70 bg-surface/60 backdrop-blur-sm p-2">
          {(['light', 'dark'] as const).map((mode) => {
            const active = theme === mode;
            return (
              <button
                key={mode}
                type="button"
                onClick={() => chooseTheme(mode)}
                aria-pressed={active}
                className={`pressable min-h-11 rounded-xl text-[13px] font-semibold transition focus-visible:outline-none focus-visible:shadow-focus-ring ${
                  active
                    ? 'bg-primary text-white'
                    : 'bg-surface-muted text-ink-muted hover:bg-primary-soft hover:text-primary'
                }`}
              >
                {mode === 'light' ? '라이트' : '다크'}
              </button>
            );
          })}
        </div>
      </section>

      <section aria-labelledby="profile-payout-heading" className="space-y-2">
        <h2 id="profile-payout-heading" className="px-0.5 text-[11px] font-semibold uppercase tracking-[0.04em] text-ink-muted">정산</h2>
        <PayoutAccountCard />
      </section>

      <PushNotificationCard />

      <section aria-labelledby="profile-account-heading" className="space-y-2">
        <h2 id="profile-account-heading" className="px-0.5 text-[11px] font-semibold uppercase tracking-[0.04em] text-ink-muted">계정</h2>
        <div className="overflow-hidden rounded-[22px] border border-surface-border/70 bg-surface/60 backdrop-blur-sm">
          <div className="flex items-center gap-3 px-3.5 py-3">
            <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[11px] bg-success-soft text-success-strong">
              <Icon name={providerIconName(user.provider)} size={17} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[14px] font-semibold tracking-[-0.2px] text-ink">{providerLabel} 연결</span>
              <span className="mt-0.5 block truncate text-[11px] text-ink-muted">현재 로그인 방식</span>
            </span>
            <Badge tone="primary">연결됨</Badge>
          </div>
          <div className="flex items-center gap-3 border-t border-surface-border px-3.5 py-3">
            <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[11px] bg-surface-muted text-ink-faint">
              <Icon name="lock" size={17} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[14px] font-semibold tracking-[-0.2px] text-ink">계정 보안</span>
              <span className="mt-0.5 block truncate text-[11px] text-ink-muted">로그아웃과 회원 탈퇴를 관리해요</span>
            </span>
          </div>
        </div>
      </section>

      <button
        type="button"
        onClick={() => logoutMut.mutate()}
        disabled={logoutMut.isPending}
        className="w-full rounded-[22px] border border-surface-border/70 bg-surface/60 backdrop-blur-sm px-3.5 py-3 text-center text-[13px] font-semibold text-error transition-colors hover:bg-error-soft disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:shadow-focus-ring"
      >
        {logoutMut.isPending ? '로그아웃 중…' : '로그아웃'}
      </button>

      <div className="text-center">
        <button
          type="button"
          onClick={() => setConfirmingDelete(true)}
          className="rounded-md px-1 text-small text-ink-muted underline underline-offset-2 transition-colors duration-150 hover:text-error focus-visible:outline-none focus-visible:shadow-focus-ring"
        >
          회원 탈퇴
        </button>
      </div>

      <Dialog
        open={confirmingDelete}
        title="정말 탈퇴할까요?"
        description="계정이 삭제되고 다시 로그인할 수 없어요. 등록한 정산 계좌는 즉시 삭제되며, 모임·채팅 기록에는 '탈퇴한 사용자'로 남아요. 이 작업은 되돌릴 수 없어요."
        confirmLabel={deleteMut.isPending ? '탈퇴 중…' : '탈퇴하기'}
        cancelLabel="계속 사용할게요"
        confirmTone="danger"
        confirmDisabled={deleteMut.isPending}
        onConfirm={() => deleteMut.mutate()}
        onCancel={() => setConfirmingDelete(false)}
      />
    </section>
  );
}
